import { PlusOutlined } from '@ant-design/icons'
import type { CalculatedTaskStatus, Day, Home } from '@lmc/ts-common'
import { Button, Card, StatusBadge } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { Spinner } from 'components/generic'
import { TODOS } from 'components/routes/todos'
import dayjs from 'dayjs'
import { NAMESPACE } from 'locale'
import qs from 'qs'
import type { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import useDailyTasks, {
  TempCalculatedTaskStatus
} from 'screens/Todos/NewDashboard/hooks/useDailyTasks'
import type { AllFilters } from 'screens/Todos/NewDashboard/hooks/utils'
import { ADD_TODO_HASH_ROUTE } from 'screens/Todos/NewManager/utils'
import styled from 'styled-components'
import { toDosConstants } from '../../../locale/toDos'
import { EmptyItems } from './CardComponents'
import { CardTitle } from './CardComponents/CardTitle'

const today = dayjs()

type Props = {
  hasPro?: boolean
}

const ToDosCard: FC<Props> = ({ hasPro }) => {
  const { days, isLoading } = useDailyTasks({
    rangePicker: [today, today]
  })

  const { t } = useTranslation(NAMESPACE.TODOS)

  const preferredPeopleNominative: string = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative || ''
  )

  const day = days[0] as Day | undefined
  const totalTasks = getTotalTodos(day?.counts)

  return (
    <CardStyled className="lmc-home-card">
      <CardContent>
        <Link to={TODOS.DASHBOARD}>
          <CardTitle title={t(toDosConstants.TODAYS_TODOS)} />
        </Link>
        {isLoading ? (
          <Spinner />
        ) : totalTasks > 0 ? (
          <Content className="lmc-home-card-body-ss">
            <Text weight="bold">{`${totalTasks} `}</Text>
            <Text>{t(toDosConstants.TODOS_FOR_TODAY)}</Text>
            <StatusesContainer>
              {Object.keys(TempCalculatedTaskStatus)?.map((statusType) => (
                <Link
                  key={statusType}
                  to={`${TODOS.DASHBOARD}?${getDashboardQueryParams(
                    statusType as CalculatedTaskStatus
                  )}`}
                >
                  <StatusBadge
                    key={statusType}
                    status={statusType as CalculatedTaskStatus}
                    count={day?.counts[statusType as CalculatedTaskStatus] || 0}
                    leftText={false}
                    showStatus
                  />
                </Link>
              ))}
            </StatusesContainer>
          </Content>
        ) : (
          <EmptyStateWrapper>
            <EmptyItems size="sm" items={4} />
            {hasPro && (
              <InfoWrapper>
                <StyledText>
                  {t(toDosConstants.CREATE_A_TODO, {
                    variable: preferredPeopleNominative
                  })}
                </StyledText>
                <HereWrapper>
                  <StyledText>
                    {t(toDosConstants.SEE_TODOS)}
                    <Link to={TODOS.DASHBOARD}>
                      <StyledTextHere>{t(toDosConstants.HERE)}</StyledTextHere>
                    </Link>
                  </StyledText>
                </HereWrapper>
              </InfoWrapper>
            )}
          </EmptyStateWrapper>
        )}
      </CardContent>
      <Buttons className="lmc-home-card-footer">
        <Link to={`${TODOS.DASHBOARD}#${ADD_TODO_HASH_ROUTE}`}>
          <Button icon={<PlusOutlined />} variant="primary">
            {t(toDosConstants.ADD_TO_DO)}
          </Button>
        </Link>
      </Buttons>
    </CardStyled>
  )
}

export const getDashboardQueryParams = (
  status: CalculatedTaskStatus
): string => {
  const filter: Required<Pick<AllFilters, 'status'>> & {
    rangePicker: [string, string]
  } = {
    rangePicker: [today.toISOString(), today.toISOString()],
    status: [status]
  }

  return qs.stringify(filter)
}

const getTotalTodos = (counts: Day['counts'] | undefined): number => {
  if (!counts) {
    return 0
  }
  let total = 0

  Object.keys(counts).forEach((status) => {
    total += counts?.[status as keyof typeof counts] || 0
  })

  return total
}

const EmptyStateWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`

const CardStyled = styled(Card)`
  padding: 24px 24px;
  border-radius: 24px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`
const Content = styled.div`
  min-height: fit-content;
`
const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`
const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`
const StatusesContainer = styled.div`
  margin-top: 16px;
  width: fit-content;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  column-gap: 48px;
  row-gap: 8px;
  > * {
    width: fit-content;
  }
`

const InfoWrapper = styled.div`
  background-color: #fff8fa;
  border-radius: 16px;
  padding: 16px;
  margin-bottom: 16px;
  max-width: 240px;
  display: flex;
  flex-direction: column;
`

const StyledText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color4};
`

const StyledTextHere = styled(Text)`
  color: ${({ theme }) => theme.color.status.popout};
  text-decoration: underline;
  cursor: pointer;
`

const HereWrapper = styled.div`
  display: flex;
  margin-top: 20px;
`

export default ToDosCard
